import { Button, Heading, Text } from "@/design-system";
import { t } from "@/context/language";
import { motion } from "framer-motion";

type WhatsappBlockedSectionProps = {
  onCtaClick: () => void;
};

/**
 * Faixa laranja da home: o celular com "Esta conta não pode usar o WhatsApp"
 * é a dor; a API oficial da Meta é a resposta. No desktop o celular fica à
 * esquerda; no celular o texto vem primeiro para o CTA não descer.
 *
 * Contraste (DESIGN.md §4): sobre `ds-brand-500` só entra conteúdo claro e
 * grande. O título é h1 visual e o parágrafo sai em `text-ds-h3` com peso
 * normal (24px/400, Sora) — 24px é o piso de "texto grande" da WCAG, então
 * 3,45:1 passa sem negrito. O botão é `secondary` (o `primary`, brand-600,
 * some contra o laranja) e cresce além do `lg` por ser o único CTA da faixa.
 */
export function WhatsappBlockedSection({
  onCtaClick,
}: WhatsappBlockedSectionProps) {
  const copy = t.whatsappBlocked;

  return (
    <div className="grid grid-cols-1 items-center gap-10 py-16 sm:py-20 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16 lg:py-24">
      <motion.div
        className="flex flex-col gap-6 text-left lg:gap-8"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <Text
          as="span"
          size="lead"
          weight="semibold"
          className="text-ds-on-solid uppercase tracking-wide"
        >
          {copy.eyebrow}
        </Text>
        <Heading
          level={2}
          id="whatsapp-bloqueado-title"
          className="text-ds-on-solid md:text-ds-h1"
        >
          {copy.title}
        </Heading>
        <Text
          as="p"
          className="text-ds-on-solid max-w-xl text-ds-h3 font-normal"
        >
          {copy.description}
        </Text>
        <div className="flex flex-col items-start gap-3">
          <Button
            variant="secondary"
            size="lg"
            onClick={onCtaClick}
            className="min-h-16 px-10 text-ds-h4 font-semibold"
          >
            {copy.cta}
          </Button>
          <Text
            as="span"
            size="lead"
            weight="semibold"
            className="text-ds-on-solid"
          >
            {copy.ctaNote}
          </Text>
        </div>
      </motion.div>

      <motion.div
        className="flex justify-center lg:order-first lg:justify-start"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.15 }}
        viewport={{ once: true }}
      >
        <img
          src="/landing-page/whatsapp-bloqueado-conta-nao-pode-usar.webp"
          alt={copy.imageAlt}
          width="480"
          height="992"
          loading="lazy"
          decoding="async"
          className="h-auto w-56 sm:w-64 lg:w-72"
        />
      </motion.div>
    </div>
  );
}
